import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormikProps } from 'formik';
import { useIntl } from 'react-intl';
import { JobMessages } from 'src/intl/JobMessages';
import { JobDetailsFormFields } from './UpdateJobDetailsModal';
import './UpdateJobDetailsForm.scss';
import { Input } from 'src/components/reusable-components/Input';
import { InputSelect } from 'src/components/reusable-components/InputSelect';
import { IonLoading } from '@ionic/react';
import { AutocompleteSelectItem } from '../../reusable-components/AutocompleteSelect';
import { fetchLogTagsByOrgId } from '../../../redux/logTags/thunk/logTags';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../redux/types';
import { AnyAction } from 'redux';
import { selectAuthUserInfo } from '../../../redux/auth/selectors';
import { selectLogTags } from '../../../redux/logTags/selector';
import { TryFetchLogTagsStatus } from '../../../redux/logTags/types';
import { logTagsActions } from '../../../redux/logTags/actions';
import { AutocompleteSelect } from '../../reusable-components/AutocompleteSelect';

interface UpdateJobDetailsFormProps {
  /**
   * State, handlers, and helpers made available to form component or render prop of.
   */
  formikProps: FormikProps<JobDetailsFormFields>;
  originalFilename: string;
}

const UpdateJobDetailsForm: React.FC<UpdateJobDetailsFormProps> = ({ formikProps, originalFilename }) => {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
  const initialTimezone = useRef(formikProps.values.timezone);
  const user = useSelector(selectAuthUserInfo);

  useEffect(() => {
    if (user?.org) {
      dispatch(fetchLogTagsByOrgId(user.org));
    } else {
      dispatch(logTagsActions.tryFetchLogTagsFailure);
    }
  }, [dispatch, user?.org]);

  const timezoneOptions: string[] = useMemo(() => {
    try {
      // Attempt to present only the timezones that are relevant to the user based on their locale. These APIs are not fully consistent or supported across all browsers.
      const canonical = new Intl.Locale(locale);
      const tz: undefined | string[] = !!(canonical as any).getTimeZones
        ? (canonical as any).getTimeZones()
        : (canonical as any).timeZones;
      if (Array.isArray(tz)) {
        if (!tz.includes(initialTimezone.current)) tz.unshift(initialTimezone.current); // The TZ that was originally selected is always an option.
        if (!tz.includes('UTC')) tz.push('UTC'); // UTC is always an option.
        return tz;
      }
    } catch {}
    try {
      return (Intl as any).supportedValuesOf('timeZone'); // Fall back to all timezones.
    } catch {}
    return undefined;
  }, [locale]);

  const timezoneSelectOptions = useMemo(() => {
    return timezoneOptions?.map((tz: string) => {
      return { value: tz, label: tz };
    });
  }, [timezoneOptions]);

  const tryFetchLogTagsState = useSelector(selectLogTags);
  const filteredLogTagItems = useMemo(() => {
    const filteredLogTagItems =
      tryFetchLogTagsState.logTags
        ?.map(logTag => {
          return new AutocompleteSelectItem(logTag._id, logTag.tag);
        })
        ?.filter((logTagItem: AutocompleteSelectItem) => logTagItem.id !== formikProps.values.logTagItem.id) ?? [];

    filteredLogTagItems.unshift(new AutocompleteSelectItem('', ''));

    return filteredLogTagItems;
  }, [formikProps.values.logTagItem.id, tryFetchLogTagsState.logTags]);

  const onItemSelected = useCallback(
    (logTagItem: AutocompleteSelectItem) => {
      formikProps.setFieldValue('logTagItem', logTagItem);
    },
    [formikProps],
  );

  const isLoading =
    tryFetchLogTagsState.status === TryFetchLogTagsStatus.NotStartedYet ||
    tryFetchLogTagsState.status === TryFetchLogTagsStatus.InProgress;

  if (isLoading) {
    return <IonLoading isOpen={true} spinner="circles" />;
  }

  return (
    <>
      <Input
        value={originalFilename}
        label={formatMessage(JobMessages.uploadFormLogFileLabel)}
        id="update-job-details-filename"
        disabled
      />
      <Input
        value={formikProps.values.description}
        onChange={formikProps.handleChange}
        label={formatMessage(JobMessages.uploadFormDescriptionLabel)}
        name="description"
        id="update-job-details-description"
        required
        errorText={formikProps.errors?.description}
      />

      <AutocompleteSelect
        value={formikProps.values.logTagItem.text}
        options={filteredLogTagItems}
        label={formatMessage(JobMessages.uploadFormTagLabel)}
        searchModalTitle={formatMessage(JobMessages.uploadFormTagSearchModalTitle)}
        searchBarPlaceholder={formatMessage(JobMessages.searchOrAddNewTagPlaceholder)}
        onItemSelected={item => onItemSelected(item)}
      />

      <Input
        value={formikProps.values.siteName}
        onChange={formikProps.handleChange}
        onBlur={formikProps.handleBlur}
        label={formatMessage(JobMessages.uploadFormSiteNameLabel)}
        name="siteName"
        id="update-job-details-site-name"
      />

      <Input
        value={formikProps.values.heatExchangerModelNumber}
        onChange={formikProps.handleChange}
        onBlur={formikProps.handleBlur}
        label={formatMessage(JobMessages.uploadFormHXModelNumberLabel)}
        name="heatExchangerModelNumber"
        id="update-job-details-hx-model-number"
      />
      {timezoneOptions && (
        <InputSelect
          value={formikProps.values.timezone}
          options={timezoneSelectOptions}
          label={formatMessage(JobMessages.uploadFormTimeZoneLabel)}
          onChange={e => formikProps.setFieldValue('timezone', e)}
          name="timezone"
          id="update-job-details-timezone"
        />
      )}
    </>
  );
};

export default UpdateJobDetailsForm;
